import { forwardRef, useEffect, useRef, useState } from "react";
import { t } from "../i18n";

// Text onto the clipboard, and "Copied" on what did it for a moment. Where
// there is no clipboard nothing is said: the text is on screen beside it, to
// copy by hand. (A reviewer's invite link and an agent's token keep their
// "Copied" until the next one — Review, AgentEndpoint — and are not this.)

const shownFor = 1500;

export function useCopy(text: string): [boolean, () => Promise<void>] {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      return;
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), shownFor);
  };
  return [copied, copy];
}

/**
 * A Copy button for `text` — `label` in place of "Copy" where there are
 * several (Copy code, Copy link); a click on it does nothing else (not a
 * link it sits in, nor a row's). `icon`, it is
 * an icon beside what it copies, named by `label` for a screen reader, and
 * once it has copied a check and "Copied" beside it — said on the page, not
 * in a tooltip a touch screen cannot show.
 */
export const CopyButton = forwardRef<HTMLButtonElement, { text: string; label?: string; icon?: boolean }>(function CopyButton(
  { text, label, icon = false },
  ref,
) {
  const [copied, copy] = useCopy(text);
  if (icon) {
    return (
      <>
        <button
          ref={ref}
          type="button"
          className={`icon-copy${copied ? " is-copied" : ""}`}
          aria-label={label ?? t("common:copy")}
          title={label ?? t("common:copy")}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void copy();
          }}
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            {copied ? (
              <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            ) : (
              <>
                <rect x="8.5" y="8.5" width="11" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
                <path d="M15.5 8.5V6.5a2 2 0 0 0-2-2h-7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h2" fill="none" stroke="currentColor" strokeWidth="1.8" />
              </>
            )}
          </svg>
        </button>
        {copied && (
          <span className="icon-copy-done" role="status">
            {t("common:copied")}
          </span>
        )}
      </>
    );
  }
  return (
    <button
      ref={ref}
      type="button"
      className="small"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void copy();
      }}
    >
      {copied ? t("common:copied") : (label ?? t("common:copy"))}
    </button>
  );
});
